import { Alert, AlertType } from '@design/components/alert/Alert';
import { Button, ButtonProps } from '@design/components/button/Button';
import { useGetKgInsightSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/useGetKgInsightSubscription';
import { usePostKgInsightSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/usePostKgInsightSubscription';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { getTrackButtonProps } from '@terminal/widgets/insight-widget/components/insight-summary/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './TrackEarningsPreviewButton.module.scss';

const TRACKING_TOAST_ID = 'earnings-preview-tracking-toast';
const TRACKING_TOAST_SCOPE = 'earnings-preview-tracking';

export interface TrackEarningsPreviewButtonProps {
  entityTag: string;
}

export const TrackEarningsPreviewButton = ({
  entityTag,
}: TrackEarningsPreviewButtonProps) => {
  const { t } = useTranslation(['earnings', 'insights']);
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetKgInsightSubscription({ entityTag });
  const { mutate: updateSubscription, isPending: isSubscriptionPending } =
    usePostKgInsightSubscription();
  const { trackAlertTracked, trackAlertUntracked } = usePosthogEvents();
  const { showToast, removeToast, clearScopedToasts } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
    clearScopedToasts: state.clearScopedToasts,
  }));

  useEffect(
    () => () => clearScopedToasts(TRACKING_TOAST_SCOPE),
    [clearScopedToasts]
  );

  if (isSubscriptionLoading) {
    return null;
  }

  const isTracked = subscription?.tracking_status === 'active';

  const handleTrackClick = () => {
    const shouldTrack = !isTracked;
    const trackEvent = shouldTrack ? trackAlertTracked : trackAlertUntracked;

    trackEvent(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW, {
      source_id: entityTag,
      source_type: 'kg-insight',
    });

    updateSubscription(
      {
        entity_tag: entityTag,
        source_type: 'kg-insight',
        track: shouldTrack,
      },
      {
        onSuccess: () => {
          if (!shouldTrack) {
            clearScopedToasts(TRACKING_TOAST_SCOPE);
            return;
          }

          showToast({
            id: TRACKING_TOAST_ID,
            scope: TRACKING_TOAST_SCOPE,
            timeout: 5000,
            position: ToastPosition.TopRight,
            content: (
              <Alert
                data-testid="earnings-preview-tracking-confirmation"
                type={AlertType.Success}
                message={t('earnings:tracking.confirmation')}
                onClose={() => removeToast(TRACKING_TOAST_ID)}
              />
            ),
          });
        },
      }
    );
  };

  const buttonProps: ButtonProps = getTrackButtonProps(isTracked, t);

  return (
    <Button
      {...buttonProps}
      className={isTracked ? styles.trackedButton : styles.button}
      data-testid="earnings-preview-track-button"
      disabled={isSubscriptionPending}
      isLoading={isSubscriptionPending}
      onClick={handleTrackClick}
    />
  );
};
